import { memo, ReactElement, useCallback, useEffect, useState } from 'react';
import { Document, Page } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import { PDFDocumentProxy } from 'pdfjs-dist/types/src/display/api';
import { useHttpLoading } from 'shell/hooks/use-http-loading';
import { Controls, PositionValue } from './controls';

const options = {
  cMapUrl: 'cmaps/',
  cMapPacked: true,
};

interface ViewerPdfProps {
  data: string;
  name: string;
  orientation?: number;
  downloadable?: boolean;
  printable?: boolean;
  fixedDimension?: number;
  positionControls?: PositionValue;
}

/**
 * Componente che renderizza un PDF
 * @param {object} props props
 * @param {string} props.data the base64 to render
 * @param {string} props.name the name of the file
 * @param {number} props.orientation the orientation
 * @param {boolean} props.downloadable flag to indicate download or not
 * @param {boolean} props.printable flag to indicate print or not
 * @returns {React.ReactComponentElement} React Component
 */
const ViewerPDFComponent = ({
  data,
  name,
  orientation,
  downloadable,
  printable,
  fixedDimension,
  positionControls,
}: ViewerPdfProps): ReactElement => {
  const [statePdfViewer, setNumberPages] = useState({ totalPages: 0, page: 1, loaded: false });
  const httpLoading = useHttpLoading();

  useEffect(() => {
    document.body.className = `${document.body.className} toPrint`;
    // httpLoading.addLoading();

    return () => {
      document.body.className = document.body.className.replace(' toPrint', '');
    };
  }, []);

  const [dimension, setDimension] = useState(0);

  const onDocumentLoadSuccess = useCallback(
    async ({ numPages }: PDFDocumentProxy) => {
      setNumberPages({ totalPages: numPages, page: 1, loaded: true });
      // httpLoading.removeLoading();
    },
    [setNumberPages, httpLoading.removeLoading]
  );

  const { page, totalPages, loaded } = statePdfViewer;

  useEffect(() => {
    let element: HTMLElement | undefined | null;
    if (loaded) {
      element = document.getElementById('elementToPrint');
      if (element) {
        document.body.append(element);
      }
    }

    return () => {
      if (element) {
        element.remove();
      }
    };
  }, [loaded]);

  return (
    <>
      <div className="position-relative">
        <div className="noPrint">
          <Controls
            file={data}
            totalPages={totalPages}
            onNext={(previousPage) => setNumberPages({ ...statePdfViewer, page: previousPage })}
            onBack={(nextPage) => setNumberPages({ ...statePdfViewer, page: nextPage })}
            page={page}
            name={name}
            printable={printable}
            downloadable={downloadable}
            setDimension={(width) => {
              setDimension((oldState) => oldState + width);
            }}
            positionControls={positionControls}
          />
        </div>
        <Document
          className="noPrint"
          file={`data:application/pdf;base64, ${data}`}
          onLoadSuccess={onDocumentLoadSuccess}
          options={options}
          rotate={orientation}
          loading="Caricando il PDF."
          noData="Nessun PDF da mostrare."
          error="Errore nel caricamento del pdf."
        >
          <Page
            key={`page_${page}`}
            pageNumber={page}
            width={fixedDimension || dimension}
            onLoadSuccess={(pageData) => {
              setDimension(pageData.width);
            }}
          />
        </Document>
      </div>
    </>
  );
};

ViewerPDFComponent.displayName = 'Visualizzatore di PDF';

const ViewerPDF = memo(ViewerPDFComponent) as typeof ViewerPDFComponent;

export default ViewerPDF;
